import { HttpClient, HttpContext, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import {
  CollectionOfCountry,
  DefaultCountry,
  DefaultDocumentLanguage,
  DefaultLanguage,
  DefaultYear,
} from "./generated";
import { ConfigService } from "../config/config.service";
import { LanguageSelectionResult } from "../models/language-modal-dialog";
import { SelectKeyValue } from "../models/select-key-value";
import { WebStorage } from "../services/web-storage";

@Injectable({
  providedIn: "root",
})
export class PiaContextService {
  configService = inject(ConfigService);
  http = inject(HttpClient);
  storage = inject(WebStorage);

  private getResponse(context?: HttpContext) {
    const url = `${this.configService.piaAPIOrigin}/Country?$expand=Languages/Years,DocumentLanguages`;

    const headers = new HttpHeaders({
      accept: "application/json",
    });

    return this.http.get<{ d?: CollectionOfCountry }>(url, {
      headers,
      context,
    });
  }

  public getCountries(context?: HttpContext) {
    const response = this.getResponse(context);

    return response.pipe(
      map((countries) => {
        const countrySelectList = countries?.d?.results?.reduce(
          (currentCountrySelectList, country: DefaultCountry) => {
            if (country.ID && country.Title) {
              currentCountrySelectList.push({
                key: country.ID,
                text: country.Title,
              });
            }

            return currentCountrySelectList;
          },
          [] as SelectKeyValue[],
        );

        return countrySelectList ?? [];
      }),
    );
  }

  public getLanguages(
    { countryID }: { countryID: string },
    context?: HttpContext,
  ) {
    const response = this.getResponse(context);
    return response.pipe(
      map((countries) => {
        const languageSelectList = countries?.d?.results
          ?.filter(
            (country: DefaultCountry) =>
              country.ID === countryID.toUpperCase() &&
              Boolean(country.Languages?.results?.length),
          )
          .reduce((currentLanguageSelectList, country: DefaultCountry) => {
            country.Languages?.results
              // need filter and if check in forEach to prevent having empty objects
              // and for type safety
              ?.filter((lang) => Boolean(lang.ID && lang.Title))
              .forEach((lang) => {
                if (lang.ID && lang.Title) {
                  currentLanguageSelectList.push({
                    key: lang.ID,
                    text: lang.Title,
                  });
                }
              });
            return currentLanguageSelectList;
          }, [] as SelectKeyValue[]);
        return languageSelectList ?? [];
      }),
    );
  }

  public getLanguageSelectionForCountry({
    countryID,
    preferredLanguageID,
  }: {
    countryID: string;
    preferredLanguageID: string;
  }): Observable<LanguageSelectionResult> {
    return this.getLanguages({ countryID }).pipe(
      map((availableLanguages) => {
        // Check if preferred language exists in available languages
        const preferredLanguageExists = availableLanguages.some(
          (lang) =>
            lang.key.toUpperCase() === preferredLanguageID.toUpperCase(),
        );

        // If preferred language is available, use it; otherwise use first available
        const selectedLanguageID = preferredLanguageExists
          ? preferredLanguageID.toUpperCase()
          : (availableLanguages[0]?.key ?? null);

        return {
          wasPreferredLanguageAvailable: preferredLanguageExists,
          selectedLanguageID,
          availableLanguages,
        };
      }),
    );
  }

  public getYearsByCountryIdAndLanguageId(
    { countryID, languageID }: { countryID: string; languageID: string },
    context?: HttpContext,
  ) {
    const response = this.getResponse(context);

    return response.pipe(
      map((countries) => {
        const yearsResult = countries?.d?.results
          ?.filter((country: DefaultCountry) => {
            return country.ID === countryID.toUpperCase();
          })
          .reduce((acc: number[], country: DefaultCountry) => {
            const langResult = country?.Languages?.results?.find(
              (lang: DefaultLanguage) => lang.ID === languageID.toLowerCase(),
            );
            if (langResult?.Years?.results?.length) {
              const years = langResult.Years.results
                .map((year: DefaultYear) => year.ID)
                .filter(Boolean) as number[];
              acc = [...acc, ...years];
            }

            return acc;
          }, []);

        if (yearsResult?.length) {
          return yearsResult;
        }

        return [];
      }),
    );
  }

  public getDefaultLanguageIdByCountryId({
    countryID,
    preferredLanguageID,
  }: {
    countryID: string;
    preferredLanguageID?: string;
  }): Observable<string | null> {
    return this.getLanguages({ countryID }).pipe(
      map((languages) => {
        if (preferredLanguageID) {
          const langFound = languages.find(
            (lang) =>
              lang.key.toLowerCase() === preferredLanguageID.toLowerCase(),
          )?.key;
          if (langFound) {
            return langFound;
          }
        }
        const fallbackLang =
          languages.find((_lang, index) => index === 0)?.key ?? null;
        return fallbackLang;
      }),
    );
  }

  getDocumentLanguagesByCountryAndDefaultSelectedByLanguage(
    {
      countryID,
      languageID,
    }: {
      countryID: string;
      languageID: string;
    },
    context?: HttpContext,
  ): Observable<{ primary: SelectKeyValue[]; secondary: SelectKeyValue[] }> {
    return this.getResponse(context).pipe(
      map((response?: { d?: CollectionOfCountry }) => {
        if (!response) {
          console.error("document languages response error");
          return { primary: [], secondary: [] };
        }

        const documentLanguages: {
          primary: SelectKeyValue[];
          secondary: SelectKeyValue[];
        } = {
          primary: [],
          secondary: [],
        };

        const countryObject = response.d?.results?.find(
          (country: DefaultCountry) =>
            country.ID?.toLowerCase() === countryID.toLowerCase(),
        );
        const countryDocumentLanguages =
          countryObject?.DocumentLanguages?.results;
        const countryLanguages = countryObject?.Languages?.results;
        const countryLanguageIds = countryLanguages?.map(
          (countryLanguage) => countryLanguage.ID,
        );
        const primaryDocumentLanguages = countryDocumentLanguages?.filter(
          (countryDocumentLanguage: DefaultDocumentLanguage) =>
            countryLanguageIds?.includes(countryDocumentLanguage.ID),
        );
        const secondaryDocumentLanguages = countryDocumentLanguages?.filter(
          (countryDocumentLanguage: DefaultDocumentLanguage) =>
            !countryLanguageIds?.includes(countryDocumentLanguage.ID),
        );

        documentLanguages.primary = primaryDocumentLanguages?.length
          ? primaryDocumentLanguages.map((primaryDocumentLanguage) => ({
              key: primaryDocumentLanguage.ID as string,
              text: primaryDocumentLanguage.Title as string,
            }))
          : [];
        documentLanguages.secondary = secondaryDocumentLanguages?.length
          ? secondaryDocumentLanguages.map((secondaryDocumentLanguage) => ({
              key: secondaryDocumentLanguage.ID as string,
              text: secondaryDocumentLanguage.Title as string,
              ...(languageID && secondaryDocumentLanguage?.ID === languageID
                ? { selected: true }
                : null),
            }))
          : [];

        return documentLanguages;
      }),
    );
  }

  /**
   * Returns the latest available year for a given country and language as an Observable<number>.
   */
  public getLatestYearByCountryIdAndLanguageId(params: {
    countryID: string;
    languageID: string;
  }): Observable<number> {
    return this.getYearsByCountryIdAndLanguageId(params).pipe(
      map((years) =>
        years && years.length ? Math.max(...years) : new Date().getFullYear(),
      ),
    );
  }
}
